/**
 * WHAT A SERVICE COSTS US. The one answer, and the only one anybody may ask.
 *
 * A service's cost is its TIME plus its PARTS:
 *
 *   labor  = its labor rows, each duration x rate, tier by tier
 *   parts  = each part's unit cost x quantity, ALWAYS — RULE TWO, blind to
 *            chargeability and to visibility
 *
 * IT IS NEVER `costBasis` ON THE SERVICE. That column is deliberately 0 for a
 * dynamically-priced service: such a service has no single unit cost, because
 * its cost lives in the labor rows and the parts the pricebook priced it from.
 * Three separate surfaces read the column anyway and reported the service as
 * FREE — `flatServiceCostBreakdown`, the bundle summary and the bundle contents
 * table — which is what this module exists to make impossible.
 *
 * So the input type below HAS NO `costBasis` FIELD. Not "has one and warns
 * about it": a doc comment did not stop three callers, and a field that cannot
 * be passed cannot be read. The one place an hourly figure is still accepted is
 * `hourlyCost`, for a service typed straight onto a document with no rows behind
 * it, and it is named for what it is — a RATE, multiplied by hours, never a cost
 * for the service as a whole.
 *
 * Money is rounded to the cent at the boundary, not per part: summing rounded
 * parts and rounding the sum disagree, and the totals here are read beside the
 * document's own.
 *
 * THE ONE EXCEPTION, stated rather than smuggled in: `serviceCost` takes a
 * `revenue` ARGUMENT. Some crews are not paid by the hour — they take a share
 * of what the service earns over its parts — and such a labor row's cost IS a
 * function of the price, by definition. It arrives as a named argument beside
 * the service rather than as a field on `ServiceCostSource`, so the type still
 * cannot be handed a price to mistake for a cost: the exception is visible at
 * the call site, which is where a reader looking for it will be.
 */
import type { DynamicServicePricingConfig, LaborRateProfile } from '@/types/pricebook';
import type { ServiceLaborLine } from '@/types/pricebook';

import { dynamicServiceLabor } from './dynamic-service-price';
import { costContribution } from './line-economics';
import {
  laborLinesHours,
  laborLinesTotal,
  marginShareLaborTotal,
  splitLaborLines,
} from './service-labor';

/**
 * A service, described only by what its cost can be computed FROM.
 *
 * Note what is absent: `costBasis`, `basePrice`, `laborRate`, `chargeable`,
 * `visibleToCustomer`. None of them answers "what does this service cost", and
 * three of them have been mistaken for the answer. A margin-share labor row
 * does need the price — see the module header — and it is passed to the
 * FUNCTION, never added here.
 */
export interface ServiceCostSource {
  /**
   * The service's labor rows, as the pricebook editor holds them. Resolved
   * against `laborRateProfiles` so a tiered profile costs tier by tier.
   */
  laborLines?: ServiceLaborLine[] | null;
  /**
   * A materialized dynamic service's frozen configuration, whose own
   * `laborLines` carry the time it was priced from and what that time is worth.
   * Preferred over `laborLines` when present: it is the snapshot, and the
   * snapshot is what the service was sold at.
   */
  dynamicPricingConfig?: DynamicServicePricingConfig | null;
  /**
   * Hours recorded against a service with no rows behind it — a service typed
   * straight onto a document.
   */
  estimatedHours?: number | null;
  /**
   * What one of those hours costs. A RATE, multiplied by `estimatedHours`, and
   * meaningful only together with them.
   */
  hourlyCost?: number | null;
}

/** A part the service carries. Costed identically wherever it is drawn. */
export interface ServicePartCostSource {
  /** MATERIAL / EQUIPMENT — decides which bucket the cost lands in. */
  type?: string | null;
  costBasis?: number | null;
  cost?: number | null;
  quantity?: number | null;
  /** Offered, not sold: an unselected optional part costs the job nothing. */
  isOptional?: boolean | null;
  isSelected?: boolean | null;
  /**
   * Whether the part is billed on its own line, and at what unit price. Read
   * only by `serviceCardRevenue`: a part billed separately earns money of its
   * own, which a margin share is taken from along with the service's price.
   */
  pricingTreatment?: string | null;
  basePrice?: number | null;
}

export interface ServiceCost {
  labor: number;
  /**
   * Of that labor, the part taken as a share of the margin rather than earned
   * by the hour. Carried out separately so a cost panel can say which it is
   * without re-deriving it — and so `labor - laborFromMargin` is still the
   * hourly figure every existing reader means.
   */
  laborFromMargin: number;
  /** The hours behind the labor figure — flat and dynamic services alike. */
  laborHours: number;
  material: number;
  equipment: number;
  /**
   * What the service earns over its materials and equipment — the money labor
   * is paid out of, and the basis a margin-share row takes its percentage from.
   *
   * `0` when no revenue was supplied, because without a price there is nothing
   * to measure a contribution against.
   */
  contribution: number;
  total: number;
  /**
   * Hours recorded with no rate behind them.
   *
   * Surfaced rather than swallowed: those hours cost the job something and are
   * being counted at zero, so margin reads high for a reason the operator should
   * be told about.
   */
  hoursMissingCostRate: number;
  /**
   * The service pays a margin share, but no revenue was supplied to take it
   * from, so `labor` and `total` hold the hourly answer only. A caller that
   * passes `null` on purpose adds the share itself; any other caller must treat
   * the cost as not known rather than print the share as $0.
   */
  marginShareUnpriced: boolean;
}

const num = (value: number | null | undefined): number =>
  Number.isFinite(value) ? (value as number) : 0;

const round2 = (value: number): number => Math.round(value * 100) / 100;

const isEquipment = (type: string | null | undefined): boolean =>
  String(type ?? '')
    .trim()
    .toUpperCase() === 'EQUIPMENT';

/** Offered, not sold. Costing a part nobody bought costs a job that never ran. */
const isSold = (part: ServicePartCostSource): boolean => part.isSelected !== false;

/**
 * What a catalog service's CARD sells for: its own price plus each part billed
 * separately (price x quantity). The money a margin share is taken from, along
 * with `serviceCost`'s parts — the same basis a document measures the card on
 * (`contributionOfCard`), so the pricebook and the estimate pay the same share.
 *
 * A service's part is billed separately only when the relationship says so;
 * otherwise its money is already inside the service's price. Optional parts
 * are offered, not sold.
 */
export function serviceCardRevenue(
  price: number | null | undefined,
  parts: ServicePartCostSource[],
): number {
  let revenue = num(price);
  for (const part of parts) {
    if (part.isOptional === true && part.isSelected !== true) continue;
    if (!isSold(part)) continue;
    if (part.pricingTreatment !== 'separate_charge') continue;
    revenue += num(part.basePrice) * num(part.quantity);
  }
  return round2(revenue);
}

/**
 * The service's own time, and what it is worth.
 *
 * Three sources, in the order they are trustworthy. A materialized dynamic
 * service's SNAPSHOT first — that is the time it was actually priced from, and
 * it carries its own resolved rates. Then the live labor rows, resolved against
 * the profiles. Then bare hours at an hourly cost, for a service with no rows.
 */
function labourOf(
  service: ServiceCostSource,
  laborRateProfiles: LaborRateProfile[],
): { amount: number; hours: number; marginShareRows: ServiceLaborLine[] } {
  const snapshot = service.dynamicPricingConfig;
  if (snapshot?.laborLines?.length) {
    const minutes = snapshot.laborLines.reduce(
      (total, line) => total + Math.max(0, line.durationMinutes || 0),
      0,
    );
    // A dynamic service cannot carry margin-share rows — its price is derived
    // FROM its labor, so a share of the margin would be circular, and both the
    // editor and `DynamicPricingService.preview` refuse one.
    return { amount: dynamicServiceLabor(snapshot), hours: minutes / 60, marginShareRows: [] };
  }

  const rows = service.laborLines ?? [];
  if (rows.length > 0) {
    const { hourly, marginShare } = splitLaborLines(rows, laborRateProfiles);
    return {
      amount: laborLinesTotal(hourly, laborRateProfiles),
      hours: laborLinesHours(hourly),
      marginShareRows: marginShare,
    };
  }

  const hours = Math.max(0, num(service.estimatedHours));
  return {
    amount: hours * Math.max(0, num(service.hourlyCost)),
    hours,
    marginShareRows: [],
  };
}

/**
 * What this service costs us: its time plus its parts.
 *
 * `laborRateProfiles` is required rather than optional, and empty is a
 * legitimate answer for a service whose rows are all fixed-rate. Making it
 * optional invited the caller that "does not have them handy" to omit them and
 * silently cost every tiered row at zero — which is the same failure as reading
 * `costBasis`, arrived at politely.
 */
export function serviceCost(
  service: ServiceCostSource,
  parts: ServicePartCostSource[],
  laborRateProfiles: LaborRateProfile[],
  /**
   * What the service's card sells for (`serviceCardRevenue`), or `null` when
   * the caller has no price to give. REQUIRED, so a caller cannot leave it out
   * and have a margin share silently costed at $0 — see `marginShareUnpriced`.
   */
  revenue: number | null,
): ServiceCost {
  const labour = labourOf(service, laborRateProfiles);

  let material = 0;
  let equipment = 0;
  for (const part of parts) {
    if (part.isOptional === true && part.isSelected !== true) continue;
    if (!isSold(part)) continue;
    // RULE TWO, through the canonical helper: cost x quantity, whatever the
    // part charges and whether or not the customer ever sees it.
    const cost = costContribution({
      costBasis: part.costBasis,
      cost: part.cost,
      quantity: part.quantity ?? 0,
    });
    if (isEquipment(part.type)) equipment += cost;
    else material += cost;
  }

  // The money labor is paid out of. Measured against the parts' COST rather
  // than what they are billed at, because that is what the service actually
  // spent to deliver them.
  const contribution = num(revenue) - material - equipment;
  const marginShareUnpriced = labour.marginShareRows.length > 0 && revenue === null;
  const laborFromMargin =
    labour.marginShareRows.length && !marginShareUnpriced
      ? marginShareLaborTotal(labour.marginShareRows, laborRateProfiles, contribution)
      : 0;

  const labor = round2(labour.amount + laborFromMargin);
  return {
    labor,
    laborFromMargin: round2(laborFromMargin),
    laborHours: labour.hours,
    material: round2(material),
    equipment: round2(equipment),
    contribution: round2(contribution),
    total: round2(labour.amount + laborFromMargin + material + equipment),
    // Flagged on the OUTCOME, not on one field: hours that produced no cost are
    // the thing worth warning about, however the cost was arrived at. The hours
    // here are the HOURLY rows' alone, so a margin-share service — which quotes
    // no time — never trips it.
    hoursMissingCostRate: labour.hours > 0 && round2(labour.amount) <= 0 ? labour.hours : 0,
    marginShareUnpriced,
  };
}
